import { Component, Input, OnChanges, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { Compbaser } from "../../../components/compbaser";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { TranslocoService } from "@ngneat/transloco";
import { ToastrService } from "ngx-toastr";
import { Router } from "@angular/router";
import { SmallPageSizeOptions } from "../../../common";
import { IAppSettingsWrap, IPlayerProps, ISharedContent } from "../../../interfaces";
import { ContainerService, ImportService, StoreSelectorsService } from "../../../services";
import { DialogImportingComponent } from "../../../components/dialog-importing/dialog-importing.component";
import { DialogPreviewComponent, DialogPreviewData } from "../../../components/dialog-preview/dialog-preview.component";
import * as xml2js from "xml2js";

interface ISharedScene {
    url: string;
    businessId: number;
    sceneData?: IPlayerProps;
    path: string;
    sceneId: number;
}

@Component({
    selector: "app-scene-shared-contents",
    templateUrl: "./scene-shared-contents.component.html",
    styleUrls: ["./scene-shared-contents.component.scss"],
    standalone: false
})
export class SceneSharedContentsComponent extends Compbaser implements OnInit, OnChanges {
    @ViewChild("sharedSceneModal", { read: TemplateRef }) sharedSceneModal: TemplateRef<any>;
    @ViewChild(MatPaginator) paginator: MatPaginator;

    readonly pageSizeOptions = SmallPageSizeOptions;

    loading = true;
    filter = "";
    sharedScenes: ISharedScene[] = [];
    dataSource: MatTableDataSource<any>;
    displayedColumns: string[] = ["businessId", "icon", "label", "modified", "type", "id", "items", "preview", "play"];
    stringJSPipeArgs = { toDateFromTimestamp: ["MM/DD/YY H:mm"] };
    stringJSPipeJson = { replace: ["Json.", ""] };
    stringJSPipeJson2 = { replace: [".", " "] };
    pageSize = 10;
    sharedSceneWidth;
    sharedSceneHeight;
    sharedSceneId;
    importDialog: MatDialogRef<any>;
    downloadedSharedScenes = {};
    loadingScenePreview = false;
    coreAppData: IAppSettingsWrap;
    isFullFeatures = false;

    @Input() autoOpenOnImport = false;
    @Input() selection = [];

    @Input()
    set setSharedContents(i_Contents) {
        setTimeout(() => {
            this.buildSharedContents(i_Contents);
        }, 111);
    }

    constructor(
        private translocoService: TranslocoService,
        private containerService: ContainerService,
        private importService: ImportService,
        private toastr: ToastrService,
        private router: Router,
        private storeSelector: StoreSelectorsService,
        protected override dialog: MatDialog) {
        super(dialog);
    }

    ngOnInit() {
        this.cod(
            this.containerService.downloadedSharedScenes.subscribe((v) => {
                this.downloadedSharedScenes = v || {};
            }),
        );
        this.cod(
            this.storeSelector.getCoreData().subscribe((v: IAppSettingsWrap) => {
                this.coreAppData = v;
            }),
        );
        this.cod(
            this.storeSelector.isFullFeatures().subscribe((v) => {
                this.isFullFeatures = v;
            }),
        );
    }

    ngOnChanges(changes: import("@angular/core").SimpleChanges): void {
        if (changes.selection && !changes.selection.isFirstChange()) {
            if (this.dataSource) {
                this.applyFilter();
            }
        }
    }

    clearSelection() {
        this.filter = "";
        this.applyFilter();
    }

    applyFilter() {
        if (this.dataSource) {
            this.dataSource.filter = JSON.stringify({
                businessId: this.selection,
            });
        }
    }

    private buildSharedContents(i_sharedContents: ISharedContent[]) {
        if (i_sharedContents?.length > 0) {
            this.sharedScenes = [];
            i_sharedContents.forEach((item) => {
                const sceneContent = item.data?.scenes;
                if (sceneContent?.length > 0) {
                    sceneContent.forEach((row) => {
                        if (this.selection.includes(row.businessId)) {
                            this.parseSharedScene({
                                url: item.url,
                                businessId: row.businessId,
                                sceneData: null,
                                path: row.path,
                                sceneId: row.sceneId,
                            }, row.data);
                        }
                    });
                }
            });
            // sorted by modified
            this.sharedScenes.sort((a, b) => {
                if (b.sceneData.Player.$.lastModified) {
                    if (a.sceneData.Player.$.lastModified && b.sceneData.Player.$.lastModified) {
                        return parseInt(b.sceneData.Player.$.lastModified) - parseInt(a.sceneData.Player.$.lastModified);
                    }
                    return 1;
                }
                return -1;
            });
            this.dataSource = new MatTableDataSource(this.sharedScenes);
            this.dataSource.paginator = this.paginator;
            this.dataSource.filterPredicate = this.customFilterPredicate();
        }
    }

    private parseSharedScene(i_sharedContent: ISharedScene, xmlData: string) {
        if (!xmlData) return;
        xml2js.parseString(xmlData, { charkey: "_" }, (err, sceneData: IPlayerProps) => {
            if (err || !sceneData) {
                console.log(`Corrupted shared scene (business id: ${i_sharedContent.businessId}, scene id: ${i_sharedContent.sceneId}). Error: ${err?.message}`);
                return;
            }
            sceneData.Player.$.folder = i_sharedContent.path;
            i_sharedContent.sceneData = sceneData;
            this.sharedScenes.push(i_sharedContent);
        });
    }

    async importSharedScene(item: ISharedScene) {
        const businessId = item.businessId;
        const sceneId = item.sceneId;
        this.sharedSceneWidth = parseInt(item.sceneData.Player?.Data[0]?.Layout[0]?.$?.width);
        this.sharedSceneHeight = parseInt(item.sceneData.Player?.Data[0]?.Layout[0]?.$?.height);
        const sceneName = item.sceneData.Player.$.label;

        const key = `b_${businessId}_s_${sceneId}`;
        if (!!this.downloadedSharedScenes[key]) {
            this.sharedSceneId = this.downloadedSharedScenes[key];
            this.openSharedSceneDialog();
            return;
        }

        const downloadingDialog = this.dialog.open(DialogImportingComponent, {
            height: "400px",
            width: "400px",
            panelClass: "transparentDialog",
            data: {
                showCancel: true,
                showProgress: true,
            },
            disableClose: true,
        });
        try {
            const newPlayerId = await this.importService.importScene(businessId, sceneId, sceneName, null, true);
            downloadingDialog.close();
            if (!newPlayerId) {
                this.toastr.error(this.translocoService.translate("toast.failedCreateScene", {}, this.translocoService.getActiveLang()));
                return;
            }

            this.sharedSceneId = newPlayerId;
            this.rememberDownloadedScenes(key, newPlayerId);
            this.openSharedSceneDialog();
        } catch (err) {
            console.log(err);
            setTimeout(() => {
                downloadingDialog.close();
            }, 1200);
        }
    }

    private openSharedSceneDialog() {
        this.loadingScenePreview = true;
        this.importDialog = this.dialog.open(this.sharedSceneModal, { width: "650px" });
    }

    jumpToSceneItem() {
        if (this.sharedSceneId) {
            this.router.navigateByUrl(`/editor/list/${this.sharedSceneId}`);
            this.closeModal();
        }
    }

    closeModal() {
        if (this.importDialog) {
            this.importDialog.close();
        }
        this.sharedSceneId = "";
    }

    onSceneLoaded(e) {
        setTimeout(() => {
            this.loadingScenePreview = false;
        }, 2000);
    }

    rememberDownloadedScenes(key, sceneId) {
        this.downloadedSharedScenes[key] = sceneId;
        this.containerService.downloadedSharedScenes.next(this.downloadedSharedScenes);
    }

    previewSharedScene(item: ISharedScene) {
        const sceneId = item.sceneId.toString();
        const editorWidth = parseInt(item.sceneData.Player?.Data[0]?.Layout[0]?.$?.width) || 1920;
        const editorHeight = parseInt(item.sceneData.Player?.Data[0]?.Layout[0]?.$?.height) || 1080;
        const sceneMimeType = item.sceneData.Player.$.mimeType || "mainPlayer";
        const sceneMimeShort = sceneMimeType === "mainPlayer" ? "Player" : sceneMimeType;
        const dialogData: DialogPreviewData = {
            selectedSceneId: sceneId,
            previewSceneId: sceneId,
            sceneData: item.sceneData,
            editorWidth,
            editorHeight,
            isMainScene: sceneMimeType === "mainPlayer",
            sceneMimeShort,
            coreAppData: this.coreAppData,
            isFullFeatures: this.isFullFeatures,
            businessId: item.businessId,
        };

        const dialogRef = this.dialog.open(DialogPreviewComponent, {
            width: "600px",
            data: dialogData,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result?.action === "register") {
                console.log("Registration action requested");
            }
        });
    }

    private customFilterPredicate() {
        const myFilterPredicate = (data: any, filter: string): boolean => {
            let globalMatch = !this.filter;

            if (this.filter) {
                // search all fields
                globalMatch = JSON.stringify(data).toString().toLowerCase().indexOf(this.filter.trim().toLowerCase()) !== -1;
            }

            if (!globalMatch) {
                return false;
            }

            const filterObj = JSON.parse(filter);
            let isFilter = true;
            // tslint:disable-next-line
            for (const col in filterObj) {
                let colData = data[col];
                if (col === "businessId") {
                    if (!this.selection.includes(colData)) {
                        isFilter = false;
                        break;
                    }
                } else {
                    if (typeof colData !== "undefined" && colData.toString().toLowerCase().indexOf(filterObj[col].trim().toLowerCase()) == -1) {
                        isFilter = false;
                        break;
                    }
                }
            }
            return isFilter;
        };
        return myFilterPredicate;
    }
}
